// src/pages/personal/analytics/FeedbackSection/components/CategoryDistribution/CategoryDistribution.tsx
import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Flex } from '@/components/ui/Layout';
import { TEXT_STYLES } from '@/theme';
import { CategoryChart } from './CategoryChart';
import { useResponsive } from '@/hooks/useResponsive';
import { formatPercent } from '@/utils/Formatters';
import styles from '../../FeedbackSection.module.css';

interface RatingItem {
  rating: number;
  count: number;
  percentage: number;
}

interface CategoryDistributionProps {
  data: {
    product_quality: RatingItem[];
    service: RatingItem[];
    value: RatingItem[];
  };
  isLoading?: boolean;
  period?: string; // Добавляем параметр периода
  // Пропсы для контроля внешнего выбора категории
  activeCategory?: string;
  hideButtons?: boolean;
}

const CategoryDistribution: React.FC<CategoryDistributionProps> = ({ 
  data, 
  isLoading = false, 
  period = 'month',
  activeCategory: externalCategory = 'product_quality',
  hideButtons = false
}) => {
  const { t, i18n } = useTranslation('analytics');
  const [internalCategory, setInternalCategory] = useState<'product_quality' | 'service' | 'value'>('product_quality');
  const { isMobile } = useResponsive();
  
  // Синхронизируем внутреннее состояние с внешним, если оно изменилось
  useEffect(() => {
    if (externalCategory && ['product_quality', 'service', 'value'].includes(externalCategory)) {
      setInternalCategory(externalCategory as 'product_quality' | 'service' | 'value');
    }
  }, [externalCategory]);
  
  // Используем активную категорию (либо внутреннюю, либо внешнюю)
  const activeCategory = externalCategory || internalCategory;
  
  // Заменяем функцию mapRatingFrom5To4 на mapRatingTo3Scale
  // Эта функция преобразует рейтинги из других шкал в нашу 3-балльную шкалу
  const mapRatingTo3Scale = (rating: number): number => {
    if (rating >= 4) return 3;  // 5 и 4 -> 3 (Хорошо)
    if (rating === 3) return 2; // 3 -> 2 (Есть над чем работать)
    return 1;                   // 2 и 1 -> 1 (Плохо)
  };
  
  // Обновляем цвета для различных оценок (теперь 1-3 шкала)
  const ratingColors: Record<number, string> = {
    1: '#F44336', // Красный - Плохо
    2: '#FF9800', // Оранжевый - Есть над чем работать
    3: '#4CAF50'  // Зеленый - Хорошо
  };
  
  // Соответствие эмодзи для каждой оценки (обновлено для 3-балльной шкалы)
  const ratingEmojis: Record<number, string> = {
    1: '👎', // Плохо
    2: '✋', // Есть над чем работать
    3: '👍'  // Хорошо
  };
  
  // Название категорий
  const categoryLabels = {
    product_quality: t('feedback.categories.product_quality'),
    service: t('feedback.categories.service'),
    value: t('feedback.categories.value')
  };
  
  // Подготовка данных для графика с преобразованием рейтингов
  const prepareChartData = (categoryData: RatingItem[]) => {
    const aggregatedRatings: Record<number, { count: number; percentage: number }> = {
      1: { count: 0, percentage: 0 },
      2: { count: 0, percentage: 0 },
      3: { count: 0, percentage: 0 }
    };
    
    categoryData.forEach(item => {
      const mappedRating = mapRatingTo3Scale(item.rating);
      aggregatedRatings[mappedRating].count += item.count;
      aggregatedRatings[mappedRating].percentage += item.percentage;
    });
    
    return Object.entries(aggregatedRatings).map(([rating, data]) => ({
      name: `${ratingEmojis[Number(rating)]} ${t(`feedback.ratings.r${rating}`)}`,
      value: data.count,
      percentage: data.percentage,
      color: ratingColors[Number(rating)] || '#245D33'
    }));
  };
  
  // Получаем данные активной категории
  const activeCategoryData = data ? data[activeCategory as keyof typeof data] || [] : [];
  
  // Подготавливаем данные для графика
  const chartData = useMemo(() => {
    return prepareChartData(activeCategoryData);
  }, [activeCategoryData]);
  
  // Загрузочный компонент
  const renderLoading = () => (
    <div className="h-[300px] flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#245D33] mx-auto"></div>
        <p className="mt-2 text-gray-600">{t('loading')}</p>
      </div>
    </div>
  );
  
  // Компонент для отсутствия данных
  const renderNoData = () => (
    <div className="h-[300px] flex items-center justify-center">
      <p className="text-gray-500">{t('no_data')}</p>
    </div>
  );
  
  return (
    <div className="w-full">
      <div className="p-4 border-b bg-gray-50">
        <Flex 
          justify="between" 
          align="center"
          direction={isMobile ? "column" : "row"}
          gap={isMobile ? "sm" : "none"}
        >
          <h3 className={TEXT_STYLES.h3}>{t('feedback.distribution.title')}</h3>
          {/* Показываем кнопки переключения категорий только если hideButtons=false */}
          {!hideButtons && (
            <div className={styles.categoryFilters}>
              {Object.entries(categoryLabels).map(([key, label]) => (
                <button
                  key={key}
                  className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                    activeCategory === key
                      ? 'bg-[#245D33] text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                  onClick={() => setInternalCategory(key as any)}
                >
                  {label}
                </button>
              ))}
            </div>
          )}
        </Flex>
        {/* Показываем текущую активную категорию в заголовке, если кнопки скрыты */}
        {hideButtons && (
          <p className="text-sm text-gray-500 mt-1">
            {categoryLabels[activeCategory as keyof typeof categoryLabels] || t('feedback.categories.product_quality')}
          </p>
        )}
      </div>
      
      <div className="p-4">
        {isLoading ? (
          renderLoading()
        ) : chartData.length > 0 ? (
          <Flex 
            direction={isMobile ? "column" : "row"}
            gap="md"
          >
            {/* Используем унифицированный класс chartContainer */}
            <div className={`w-full md:w-1/2 ${styles.chartContainer}`}>
              <CategoryChart 
                data={chartData} 
                period={period} // Передаем период в компонент графика
              />
            </div>
            <div className="w-full md:w-1/2 mt-4 md:mt-0">
              <h4 className="text-lg font-medium mb-2">{categoryLabels[activeCategory as keyof typeof categoryLabels]}</h4>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2">{t('feedback.distribution.rating')}</th>
                    <th className="text-right py-2">{t('feedback.distribution.count')}</th>
                    <th className="text-right py-2">{t('feedback.distribution.percentage')}</th>
                  </tr>
                </thead>
                <tbody>
                  {chartData.map((item, index) => (
                    <tr key={index} className="border-b">
                      <td className="py-2">
                        <div className="flex items-center">
                          <div 
                            className="w-3 h-3 rounded-sm mr-2" 
                            style={{ backgroundColor: item.color }}
                          ></div>
                          {item.name}
                        </div>
                      </td>
                      <td className="text-right py-2">{item.value}</td>
                      <td className="text-right py-2">
                        {formatPercent(item.percentage, 1, true, i18n.language)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Flex>
        ) : (
          renderNoData()
        )}
      </div>
    </div>
  );
};

export default CategoryDistribution;